import { Controller } from "@hotwired/stimulus"
import { loadQuill } from "../src/shared/quill/load_quill"

// Set once Quill is loaded (loadQuill); the matchers below only run inside an editor.
let Quill
let Delta

const HR_MATCHER = () => new Delta().insert({ divider: true })

// The page around the content already has its h1 (hero, post title…): a pasted or
// previously saved h1 becomes an h2.
const H1_MATCHER = (_node, delta) => {
  delta.ops.forEach((op) => {
    if (op.attributes?.header === 1) op.attributes = { ...op.attributes, header: 2 }
  })
  return delta
}

// The simple toolbar has flat bullet lists only: a pasted or saved numbered list becomes a bullet list…
const BULLET_LIST_MATCHER = (_node, delta) => {
  delta.ops.forEach((op) => {
    if (op.attributes?.list) op.attributes = { ...op.attributes, list: "bullet" }
  })
  return delta
}

// …and typing "1. " or "[ ] " no longer starts a numbered list or a checklist ("- " and "* " still start a bullet list).
const BULLET_LIST_BINDINGS = { "list autofill": { prefix: /^\s*?(-|\*)$/ } }

const EMAIL_SOCIAL_NETWORKS_MATCHER = () => new Delta().insert({ emailSocialNetworks: true })

const EMAIL_WHATSAPP_BUTTON_MATCHER = () => new Delta().insert({ emailWhatsappButton: true })

const EMAIL_SIGNATURE_LOGO_MATCHER = () => new Delta().insert({ emailSignatureLogo: true })

const EMAIL_SIGNATURE_MATCHER = (_node, delta) => {
  delta.ops.forEach((op) => {
    if (op.insert && typeof op.insert === "string") {
      op.attributes = { ...op.attributes, emailSignature: true }
    }
  })
  return delta
}

// Markers of the blocks the mailer renders: only the empty marker is saved.
const EMAIL_BLOCK_SELECTOR = ".email-social-networks, .email-whatsapp-button, .email-signature-logo"

const IFRAME_MATCHER = (_node, delta) => {
  const iframe = _node.tagName === "IFRAME" ? _node : _node.querySelector?.("iframe")
  if (!iframe?.getAttribute("src")) return delta

  return new Delta().insert({
    iframe: {
      src: iframe.getAttribute("src"),
      width: iframe.getAttribute("width"),
      height: iframe.getAttribute("height"),
      style: iframe.getAttribute("style"),
      frameborder: iframe.getAttribute("frameborder"),
      allowfullscreen: iframe.hasAttribute("allowfullscreen")
    }
  })
}

const EMPHASIS_MATCHER = (node, delta) => {
  if (!node.classList?.contains("emphasis-block")) return delta

  delta.ops.forEach((op) => {
    if (op.insert && typeof op.insert === "string") {
      op.attributes = { ...op.attributes, emphasisBlock: true }
    }
  })
  return delta
}

const CTA_LINK_MATCHER = (node, delta) => {
  if (!node.classList?.contains("btn-secondary")) return delta

  const href = node.getAttribute("href")
  if (!href) return delta

  delta.ops.forEach((op) => {
    if (op.insert && typeof op.insert === "string") {
      const attributes = { ...(op.attributes || {}) }
      delete attributes.link
      op.attributes = { ...attributes, ctaLink: href }
    }
  })
  return delta
}

// Public document pages (BasicPageDecorator) restyle a paragraph from its text: numbered clauses
// (CLAUSE_HEADING) and the version line (VERSION_LEAD). The editor tags them the same way for the
// `document_content` preview; the tag lives on the editor DOM only and is stripped on save.
const DOCUMENT_ROLE_ATTRIBUTE = "data-document-role"
const DOCUMENT_CLAUSE = /^(?:article\s+\d+\b.*|préambule\.?|\d{1,2}\.\s+.+)$/iu
const DOCUMENT_CLAUSE_MAX_LENGTH = 140
const DOCUMENT_LEAD = /^versions?\s+applicable/iu
const DOCUMENT_LEAD_MAX_LENGTH = 180

function documentRole (block) {
  if (block.tagName !== "P") return null

  const text = block.textContent.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim()
  if (text.length >= 3 && text.length <= DOCUMENT_CLAUSE_MAX_LENGTH && DOCUMENT_CLAUSE.test(text)) return "clause"
  if (text.length < DOCUMENT_LEAD_MAX_LENGTH && DOCUMENT_LEAD.test(text)) return "lead"

  return null
}

const CUSTOM_BLOT_MATCHERS = [
  ["hr", HR_MATCHER],
  ["div.email-social-networks", EMAIL_SOCIAL_NETWORKS_MATCHER],
  ["div.email-whatsapp-button", EMAIL_WHATSAPP_BUTTON_MATCHER],
  ["div.email-signature-logo", EMAIL_SIGNATURE_LOGO_MATCHER],
  ["p.email-signature", EMAIL_SIGNATURE_MATCHER],
  ["iframe", IFRAME_MATCHER],
  ["p.emphasis-block", EMPHASIS_MATCHER],
  ["div.emphasis-block", EMPHASIS_MATCHER],
  ["a.btn-secondary", CTA_LINK_MATCHER]
]

export default class extends Controller {
  static values = {
    placeholder: String,
    frontContent: { type: Boolean, default: false },
    documentContent: { type: Boolean, default: false },
    labels: { type: Object, default: {} },
    toolbar: { type: String, default: "default" },
    wordLimit: { type: Number, default: 0 },
    wordCounterTemplate: { type: String, default: "" },
    emailBlocks: { type: Object, default: {} }
  }

  static targets = [
    "editorContainer",
    "hiddenField",
    "wordCounter"
  ]

  async connect () {
    // Moving a modal shell to document.body reconnects Stimulus; without teardown
    // Quill's snow theme inserts a second toolbar.
    this.teardownQuill()

    // Quill loads on demand: a disconnect (or a reconnect) while it loads drops this connection.
    const connection = (this.connection = Symbol("connection"))
    const quillModule = await loadQuill()
    if (this.connection !== connection) return
    ;({ Quill, Delta } = quillModule)
    const { EmailSignatureLogoBlot, EmailSocialNetworksBlot, EmailWhatsappButtonBlot } = quillModule.blots

    const formats = this.formatsConfig()
    const { socialNetworks, whatsappButton, signatureLogo } = this.emailBlocksValue
    if (socialNetworks) EmailSocialNetworksBlot.networks = socialNetworks
    if (whatsappButton) EmailWhatsappButtonBlot.button = whatsappButton
    if (signatureLogo) EmailSignatureLogoBlot.logo = signatureLogo

    this.quill = new Quill(this.editorContainerTarget, {
      theme: "snow",
      placeholder: this.placeholderValue,
      ...(formats ? { formats } : {}),
      modules: {
        clipboard: {
          matchers: this.clipboardMatchers(formats)
        },
        ...(this.simpleToolbar ? { keyboard: { bindings: BULLET_LIST_BINDINGS } } : {}),
        toolbar: this.toolbarConfig()
      },
      locale: "fr"
    })

    if (this.frontContentValue) {
      this.quill.root.classList.add("ql-editor--front-content")
    }

    if (this.documentContentValue) {
      this.quill.root.classList.add("ql-editor--document-content")
    }

    if (this.simpleToolbar) {
      this.quill.root.classList.add("ql-editor--profile")
    }

    if (this.heroToolbar) {
      this.quill.root.classList.add("ql-editor--hero-intro")
    }

    // dangerouslyPasteHTML would also select the start of the text, which
    // focuses the editor on page load.
    this.quill.setContents(this.quill.clipboard.convert({ html: this.hiddenFieldTarget.value }), "silent")
    this.quill.on("text-change", () => this.updateWordCount())
    this.updateWordCount()
    if (this.documentContentValue) {
      this.quill.on("text-change", () => this.tagDocumentBlocks())
      this.tagDocumentBlocks()
    }

    this.boundSyncFormSubmission = this.syncFormSubmission.bind(this)
    this.element.closest("form")?.addEventListener("submit", this.boundSyncFormSubmission)
    if (this.defaultToolbar) {
      this._addImageUploadHandler()
      this._addDividerHandler()
    }
    if (this.frontContentValue) {
      this._addEmphasisBlockHandler()
      this._addCtaLinkHandler()
      this._addVideoHandler()
    }

    this.dispatch("ready", { detail: { quill: this.quill } })
  }

  disconnect () {
    this.connection = null
    if (this.quill) {
      this.hiddenFieldTarget.value = this.normalizeHtmlForSave(this.quill.root.innerHTML)
    }

    this.teardownQuill()
  }

  teardownQuill () {
    const form = this.element.closest("form")
    if (form && this.boundSyncFormSubmission) {
      form.removeEventListener("submit", this.boundSyncFormSubmission)
      this.boundSyncFormSubmission = null
    }

    this.quill = null
    this.element.querySelectorAll(".ql-toolbar").forEach((toolbar) => toolbar.remove())

    if (!this.hasEditorContainerTarget) return

    const container = this.editorContainerTarget
    container.classList.remove("ql-container", "ql-snow", "ql-disabled")
    container.replaceChildren()
  }

  syncFormSubmission () {
    this.hiddenFieldTarget.value = this.normalizeHtmlForSave(this.quill.root.innerHTML)
  }

  // Only writes when a tag changes: Quill observes its DOM, and an attribute alone is no text change.
  tagDocumentBlocks () {
    Array.from(this.quill.root.children).forEach((block) => {
      const role = documentRole(block)
      if (role && block.getAttribute(DOCUMENT_ROLE_ATTRIBUTE) !== role) {
        block.setAttribute(DOCUMENT_ROLE_ATTRIBUTE, role)
      } else if (!role && block.hasAttribute(DOCUMENT_ROLE_ATTRIBUTE)) {
        block.removeAttribute(DOCUMENT_ROLE_ATTRIBUTE)
      }
    })
  }

  currentWordCount () {
    return this.quill.getText().trim().split(/\s+/).filter(Boolean).length
  }

  updateWordCount () {
    if (!this.hasWordCounterTarget || this.wordLimitValue <= 0) return

    const count = this.currentWordCount()
    this.wordCounterTarget.textContent = this.wordCounterTemplateValue
      .replace("%{current}", String(count))
    this.wordCounterTarget.classList.toggle("is-over-limit", count > this.wordLimitValue)
  }

  get simpleToolbar () {
    return this.toolbarValue === "simple"
  }

  // Health guide hero intro: only what HealthGuideHelper#health_guide_intro keeps on the public page.
  get heroToolbar () {
    return this.toolbarValue === "hero"
  }

  get defaultToolbar () {
    return !this.simpleToolbar && !this.heroToolbar
  }

  clipboardMatchers (formats) {
    if (this.simpleToolbar) return [["ol", BULLET_LIST_MATCHER]]
    // The custom blots (divider, video…) are not registered when the formats are restricted.
    if (formats) return []

    return [["h1", H1_MATCHER], ...CUSTOM_BLOT_MATCHERS]
  }

  formatsConfig () {
    // Paragraphs and bullet lists: SimpleRichTextScrubber keeps the same formats on save.
    if (this.simpleToolbar) return ["bold", "italic", "list"]
    if (this.heroToolbar) return ["bold", "italic", "link", "list", "blockquote"]

    return undefined
  }

  toolbarConfig () {
    if (this.simpleToolbar) {
      return [
        ["bold", "italic"],
        [{ list: "bullet" }]
      ]
    }

    if (this.heroToolbar) {
      return [
        ["bold", "italic"],
        ["link"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote"],
        ["clean"]
      ]
    }

    const media = ["link", "image"]
    if (this.frontContentValue) media.push("video")

    const rows = [
      [{ header: [2, 3, 4, 5, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote"],
      media,
      ["divider"],
      [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
      ["clean"]
    ]

    if (this.frontContentValue) {
      rows.splice(5, 0, ["emphasisBlock", "ctaLink"])
    }

    return rows
  }

  normalizeHtmlForSave (innerHtml) {
    const container = document.createElement("div")
    container.innerHTML = innerHtml

    container.querySelectorAll("ol").forEach(ol => {
      const allBullets = Array.from(ol.children).every(
        li => li.tagName === "LI" && li.dataset.list === "bullet"
      )

      if (allBullets) {
        const ul = document.createElement("ul")

        for (const attr of ol.attributes) {
          ul.setAttribute(attr.name, attr.value)
        }

        while (ol.firstChild) {
          ul.appendChild(ol.firstChild)
        }

        ol.replaceWith(ul)
      }
    })

    container.querySelectorAll("[class*='ql-align-']").forEach((element) => {
      const match = Array.from(element.classList).find((name) => name.startsWith("ql-align-"))
      if (!match) return

      const alignment = match.replace("ql-align-", "")
      element.style.textAlign = alignment
      element.classList.remove(match)
      if (element.classList.length === 0) element.removeAttribute("class")
    })

    // Only the marker is saved: the mailer renders the block.
    container.querySelectorAll(EMAIL_BLOCK_SELECTOR).forEach((element) => {
      element.replaceChildren()
      element.removeAttribute("contenteditable")
    })

    container.querySelectorAll(`[${DOCUMENT_ROLE_ATTRIBUTE}]`).forEach((element) => {
      element.removeAttribute(DOCUMENT_ROLE_ATTRIBUTE)
    })

    container.querySelectorAll("a.btn-secondary").forEach((anchor) => {
      anchor.classList.add("btn", "btn-secondary")
    })

    return container.innerHTML
  }

  _addDividerHandler () {
    const toolbar = this.quill.getModule("toolbar")
    const dividerButton = toolbar.container.querySelector(".ql-divider")

    if (dividerButton) {
      dividerButton.innerHTML = "<svg viewBox=\"0 0 18 18\"><line class=\"ql-stroke\" x1=\"3\" x2=\"15\" y1=\"9\" y2=\"9\"></line></svg>"
      dividerButton.setAttribute("title", this.labelsValue.divider_title)
    }

    toolbar.addHandler("divider", () => {
      const range = this.quill.getSelection(true)
      if (!range) return

      let index = range.index
      const prev = this.quill.getText(Math.max(0, index - 1), 1)
      const next = this.quill.getText(index, 1)

      if (prev !== "\n" && prev !== "") {
        this.quill.insertText(index, "\n", "user")
        index += 1
      }

      this.quill.insertEmbed(index, "divider", true, "user")
      index += 1

      if (next !== "\n" && next !== "") {
        this.quill.insertText(index, "\n", "user")
      }

      this.quill.setSelection(index + 1, 0, "user")
    })
  }

  _addEmphasisBlockHandler () {
    const toolbar = this.quill.getModule("toolbar")
    const button = toolbar.container.querySelector(".ql-emphasisBlock")
    if (button) {
      button.setAttribute("title", this.labelsValue.emphasis_title)
      button.textContent = this.labelsValue.emphasis_label
    }

    toolbar.addHandler("emphasisBlock", () => {
      const range = this.quill.getSelection(true)
      if (!range) return

      const format = this.quill.getFormat(range)
      this.quill.formatLine(range.index, range.length || 1, "emphasisBlock", !format.emphasisBlock)
    })
  }

  _addCtaLinkHandler () {
    const toolbar = this.quill.getModule("toolbar")
    const button = toolbar.container.querySelector(".ql-ctaLink")
    if (button) {
      button.setAttribute("title", this.labelsValue.cta_title)
      button.textContent = this.labelsValue.cta_label
    }

    toolbar.addHandler("ctaLink", () => {
      const range = this.quill.getSelection(true)
      if (!range || range.length === 0) return

      const format = this.quill.getFormat(range)
      if (format.ctaLink) {
        this.quill.format("ctaLink", false)
        return
      }

      const href = window.prompt(
        this.labelsValue.cta_prompt,
        format.link || format.ctaLink || "https://"
      )
      if (!href) return

      this.quill.format("link", false)
      this.quill.format("ctaLink", href)
    })
  }

  _addVideoHandler () {
    const toolbar = this.quill.getModule("toolbar")
    const button = toolbar.container.querySelector(".ql-video")
    if (button) {
      button.setAttribute("title", this.labelsValue.video_title)
      button.textContent = this.labelsValue.video_label
    }

    toolbar.addHandler("video", () => {
      const input = window.prompt(this.labelsValue.video_prompt)
      if (!input) return

      const attrs = this._parseVideoInput(input)
      if (!attrs) {
        window.alert(this.labelsValue.video_unrecognized)
        return
      }

      const range = this.quill.getSelection(true)
      if (!range) return

      let index = range.index
      const prev = this.quill.getText(Math.max(0, index - 1), 1)
      const next = this.quill.getText(index, 1)

      if (prev !== "\n" && prev !== "") {
        this.quill.insertText(index, "\n", "user")
        index += 1
      }

      this.quill.insertEmbed(index, "iframe", attrs, "user")
      index += 1

      if (next !== "\n" && next !== "") {
        this.quill.insertText(index, "\n", "user")
      }

      this.quill.setSelection(index + 1, 0, "user")
    })
  }

  _parseVideoInput (input) {
    const trimmed = input.trim()
    if (!trimmed) return null

    if (/<iframe/i.test(trimmed)) {
      const doc = new DOMParser().parseFromString(trimmed, "text/html")
      const iframe = doc.querySelector("iframe")
      if (!iframe?.getAttribute("src")) return null

      return {
        src: iframe.getAttribute("src"),
        width: iframe.getAttribute("width") || "500",
        height: iframe.getAttribute("height") || "281",
        style: iframe.getAttribute("style") || "width: 500px; height: 281px;",
        frameborder: iframe.getAttribute("frameborder") || "0",
        allowfullscreen: iframe.hasAttribute("allowfullscreen")
      }
    }

    const youtube = /(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|shorts\/))([\w-]{11})/i.exec(trimmed)
    if (youtube) {
      return {
        src: `https://www.youtube.com/embed/${youtube[1]}`,
        width: "500",
        height: "281",
        style: "width: 500px; height: 281px;",
        frameborder: "0",
        allowfullscreen: true
      }
    }

    const vimeo = /vimeo\.com\/(?:video\/)?(\d+)/i.exec(trimmed)
    if (vimeo) {
      return {
        src: `https://player.vimeo.com/video/${vimeo[1]}`,
        width: "500",
        height: "281",
        style: "width: 500px; height: 281px;",
        frameborder: "0",
        allowfullscreen: true
      }
    }

    return null
  }

  _addImageUploadHandler () {
    const toolbar = this.quill.getModule("toolbar")

    toolbar.addHandler("image", () => {
      const input = document.createElement("input")
      input.setAttribute("type", "file")
      input.setAttribute("accept", "image/*")
      input.click()

      input.onchange = () => {
        const file = input.files[0]
        if (file) {
          const formData = new FormData()
          formData.append("file", file)
          formData.append("authenticity_token", document.querySelector('meta[name="csrf-token"]').content)

          fetch("/api/v1/images", {
            method: "POST",
            body: formData
          })
            .then(response => response.json())
            .then(data => {
              const range = this.quill.getSelection()
              data.forEach((image, index) => {
                this.quill.insertEmbed(range.index + index, "image", image.url)
              })
            })
            .catch(err => console.error(err))
        }
      }
    })
  }
}
